import { useEffect, useState } from 'react';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';
import UploadDocs from '../../../ui-component/UploadDocs';
import { Box, IconButton, TextField } from '@mui/material';
import { updateFormData } from 'src/redux-store/slices/constructionFormSlice';
import { useDispatch } from 'react-redux';
import { httpRequest } from 'src/services/httpRequest';
import { handleError } from 'src/redux-store/slices/errorSlice';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { Stack } from '@mui/system';
import AddIcon from '@mui/icons-material/Add';

interface UploadDocumentsFormProps {
  formData: {
    PropertyUseType?: string;
    PropertyType?: string;
    ApplicantType?: string;
    // ApplicationType?: string;
    PurposeType?: string[]; // Added PurposeType as an array
    DocumentFileUpload?: [];
  };
  ApplicationId?: string | number;
}

const UploadDocumentsForm: React.FC<UploadDocumentsFormProps> = ({
  formData,
  ApplicationId
}) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState<[]>(formData?.DocumentFileUpload || []);
  const { permit, applicationID } = useParams();
  const [inputValue, setInputValue] = useState('');
  const [otherDocumentType, setOtherDocumentType] = useState('');
  const [formValue, setFormValue] = useState({});
  const [loading, setLoading] = useState<boolean>(false);
  const [requiredDocuments, setRequiredDocuments] = useState<string[]>([]);

  const handleChange = (val: any, e: { name: string }) => {
    const data = { ...formValue };
    data[e.name] = val;
    setFormValue(data);
  };

  const getPropertyUseDocuments = (propertyUseType: string): Fields[] => {
    const documentConfigs = {
      Residential: [
        {
          fieldName: 'FloorPlanLayout',
          fieldLabel: 'Floor Plan Layout',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'PlumbingAndElectricalSchematics',
          fieldLabel: 'Plumbing and Electrical Schematics',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'FireSafetyCompliance',
          fieldLabel: 'Fire Safety Compliance',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ],
      Commercial: [
        {
          fieldName: 'FloorLayoutDesign',
          fieldLabel: 'Floor Layout Design',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'RetailSpaceDesign',
          fieldLabel: 'Retail Space Design',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'EnergyEfficiencyPlan',
          fieldLabel: 'Energy Efficiency Plan',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ],
      Industrial: [
        {
          fieldName: 'HazardousMaterialHandlingPlan',
          fieldLabel: 'Hazardous Material Handling Plan',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'SiteImpactAndEnvironmentalSafetyPlan',
          fieldLabel: 'Site Impact and Environmental Safety Plan',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'WasteManagementPlan',
          fieldLabel: 'Waste Management Plan',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'BuildingCodeCompliance',
          fieldLabel: 'Building Code Compliance',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ],
      Agriculture: [
        {
          fieldName: 'FarmLayoutPlan',
          fieldLabel: 'Farm Layout Plan',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'IrrigationAndWaterManagementPlans',
          fieldLabel: 'Irrigation and Water Management Plans',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'SustainabilityPlan',
          fieldLabel: 'Sustainability Plan',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ],
      'Mixed-use': [
        {
          fieldName: 'PublicAccessAreasAndLayouts',
          fieldLabel: 'Public Access Areas and Layouts',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'ElevatorAndAccessibilityPlans',
          fieldLabel: 'Elevator and Accessibility Plans',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ]
    };

    return documentConfigs[propertyUseType] || [];
  };

  const getPropertyTypeDocuments = (propertyType: string): Fields[] => {
    const documentConfigs = {
      Private: [
        {
          fieldName: 'LegalRestrictionsDeclarations',
          fieldLabel: 'Legal Restrictions/Declarations',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ],
      Corporate: [
        {
          fieldName: 'CorporateInsuranceDetails',
          fieldLabel: 'Corporate Insurance Details',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'LegalDocumentsContracts',
          fieldLabel: 'Legal Documents/Contracts',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ],
      Public: [
        {
          fieldName: 'PublicPropertyOwnershipProof',
          fieldLabel: 'Public Property Ownership Proof',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'PublicProjectApproval',
          fieldLabel: 'Public Project Approval',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'EnvironmentalComplianceReport',
          fieldLabel: 'Environmental Compliance Report',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'GovernmentAuthorization',
          fieldLabel: 'Government Authorization',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ],
      Partnership: [
        {
          fieldName: 'PartnershipOwnershipDocumentation',
          fieldLabel: 'Partnership Ownership Documentation',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'PartnershipAgreement',
          fieldLabel: 'Partnership Agreement',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ],
      'Trust or estate': [
        {
          fieldName: 'EstatePropertyOwnershipProof',
          fieldLabel: 'Estate Property Ownership Proof',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'TrustDeedOrEstateDocuments',
          fieldLabel: 'Trust Deed or Estate Documents',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ]
    };

    if (formData?.ApplicantType !== 'Owner') {
      documentConfigs?.Private.push({
        fieldName: 'PrivatePropertyOwnershipProof',
        fieldLabel: 'Private Property Ownership Proof',
        fieldType: 'file',
        required: true,
        dependency: handleChange
      });
    }

    return documentConfigs[propertyType] || [];
  };

  const getApplicantTypeDocuments = (applicantType: string): Fields[] => {
    const documentConfigs = {
      Owner: [
        {
          fieldName: 'ProofOfOwnership',
          fieldLabel: 'Proof of Ownership',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'LandOwnershipVerification',
          fieldLabel: 'Land Ownership Verification',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'OwnerAuthorization',
          fieldLabel: 'Owner Authorization',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ],
      Agent: [
        {
          fieldName: 'AuthorizationLetter',
          fieldLabel: 'Authorization Letter',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'AgentsProofOfIdentity',
          fieldLabel: "Agent's Proof of Identity",
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'OwnersConsentAgreement',
          fieldLabel: "Owner's Consent/Agreement",
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ],
      Contractor: [
        {
          fieldName: 'InsuranceInformation',
          fieldLabel: 'Insurance Information',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'WorkAuthorization',
          fieldLabel: 'Work Authorization',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'MaterialList',
          fieldLabel: 'Material List',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ],
      Architect: [
        {
          fieldName: 'DesignPlans',
          fieldLabel: 'Design Plans',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'SurveyReport',
          fieldLabel: 'Survey Report',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'Certifications',
          fieldLabel: 'Certifications',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'CodeCompliance',
          fieldLabel: 'Code Compliance',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'DesignApproval',
          fieldLabel: 'Design Approval',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ],
      'Tenant/Lease': [
        {
          fieldName: 'LeaseAgreement',
          fieldLabel: 'Lease Agreement',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'TenantsProofOfIdentity',
          fieldLabel: "Tenant's Proof of Identity",
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'PropertyOwnersConsent',
          fieldLabel: "Property Owner's Consent",
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ]
    };

    return documentConfigs[applicantType] || [];
  };

  // New function to handle documents based on PurposeType array
  const getPurposeTypeDocuments = (purposeTypes: string[]): Fields[] => {
    const documentConfigs = {
      'New Build': [
        {
          fieldName: 'Blueprints',
          fieldLabel: 'Blueprints',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ],
      Demolition: [
        {
          fieldName: 'EnvironmentalImpactReport',
          fieldLabel: 'Environmental Impact Report',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'SafetyPlan',
          fieldLabel: 'Safety Plan',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'HazardousMaterialPlan',
          fieldLabel: 'Hazardous Material Plan',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ],
      'Renovation/Existing Building': [
        {
          fieldName: 'StructuralIntegrityReport',
          fieldLabel: 'Structural Integrity Report',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'RenovationPlan',
          fieldLabel: 'Renovation Plan',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'CodeComplianceUpdate',
          fieldLabel: 'Code Compliance Update',
          fieldType: 'file',
          required: true,
          dependency: handleChange
        }
      ]
    };

    // If the applicant is not the owner, add ownership verification to New Build
    if (formData?.ApplicantType !== 'Owner') {
      documentConfigs?.['New Build'].push({
        fieldName: 'NewBuildLandOwnershipVerification',
        fieldLabel: 'Land Ownership Verification',
        fieldType: 'file',
        required: true,
        dependency: handleChange
      });
    }

    // Collect documents for all purpose types in the array
    let allPurposeDocs: Fields[] = [];

    // If purposeTypes is undefined or empty, return empty array
    if (!purposeTypes || !purposeTypes.length) {
      return [];
    }

    // Gather documents for each purpose type in the array
    purposeTypes.forEach((purposeType) => {
      if (documentConfigs[purposeType]) {
        allPurposeDocs = [...allPurposeDocs, ...documentConfigs[purposeType]];
      }
    });

    return allPurposeDocs;
  };

  // Fetch required documents based on form data
  useEffect(() => {
    if (formData) {
      const propertyUseFields = getPropertyUseDocuments(
        formData.PropertyUseType || ''
      );
      const propertyTypeFields = getPropertyTypeDocuments(
        formData.PropertyType || ''
      );
      const applicantFields = getApplicantTypeDocuments(
        formData.ApplicantType || ''
      );

      // Use the new purpose type function instead of application type
      const purposeTypeFields = getPurposeTypeDocuments(
        formData.PurposeType || []
      );

      // Combine and remove duplicate document fields
      const allDocuments = [
        ...propertyUseFields,
        ...propertyTypeFields,
        ...applicantFields,
        ...purposeTypeFields // Changed from applicationFields to purposeTypeFields
      ];

      // Add other document type if it exists
      if (otherDocumentType) {
        allDocuments.push({
          fieldName: otherDocumentType,
          fieldLabel: otherDocumentType,
          fieldType: 'file',
          required: true,
          dependency: handleChange
        });
      }

      // Remove duplicates based on fieldName
      const uniqueDocuments = allDocuments.filter(
        (field, index, self) =>
          index === self.findIndex((f) => f.fieldName === field.fieldName)
      );

      // Extract just the fieldLabels from the Fields objects
      setRequiredDocuments((prev) => [
        ...new Set([
          ...(prev || []),
          ...(uniqueDocuments &&
            uniqueDocuments.map((item: Fields) => item.fieldLabel))
        ])
      ]);
    }
  }, [formData, otherDocumentType]);

  const getUploadedDocuments = async () => {
    const response = await httpRequest(
      'GET',
      `/uploadPDF/?ApplicationID=${
        applicationID || ApplicationId
      }&PermitID=${permit}`
    );
    const data = await response?.data;
    if (response?.status === 'success') {
      const abstractedData =
        data &&
        data?.length &&
        data?.map((item) => {
          return {
            FileName: item?.DocumentName,
            Type: item.DocumentType,
            downloadURL: item.downloadURL,
            ApplicationDocsID: item.ApplicationDocsID
          };
        });
      setFile(abstractedData);
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  const handleAdd = () => {
    if (!inputValue.trim()) return;
    setOtherDocumentType(inputValue);
    setInputValue('');
  };

  // Update form data when files change
  useEffect(() => {

    if (file?.length > 0){
      dispatch(updateFormData({ ['DocumentFileUpload']: file }));
      file?.forEach((item: any) => {
        setRequiredDocuments((prev) => {
          if (prev?.includes(item?.Type)) {
            return prev;
          } else {
            return [...prev, item?.Type];
          }
        });
      });
    }
 
  }, [file]);

  // Fetch uploaded documents on component mount
  useEffect(() => {
    if (applicationID || ApplicationId) getUploadedDocuments();
  }, []);

  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
    >
      {loading ? (
        <LoadingOverlay />
      ) : (
        requiredDocuments.length > 0 && (
          <Box>
            <UploadDocs
              ApplicationID={applicationID || ApplicationId}
              file={file}
              staticType={requiredDocuments}
              setFile={setFile}
              setLoading={setLoading}
              reFetch={getUploadedDocuments}
              permitName="Construction"
            />
            <Box mt={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label="Add New Document Type"
                  variant="outlined"
                  fullWidth
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
                <IconButton color="primary" onClick={handleAdd}>
                  <AddIcon />
                </IconButton>
              </Stack>
            </Box>
          </Box>
        )
      )}
    </Box>
  );
};

export default UploadDocumentsForm;
